import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ModalStandard from "../../../common/components/Modal/ModalStandard";
import { useAppDispatch, useAppSelector } from "../../../common/state/hooks";
import {
  closeStandardModal,
  getShowStandardModal,
} from "../../../common/state/slice/modal/modalSlice";
import { enqueueOnboardingEvent } from "../events/eventQueue";
import { applyOnboardingOps, selectOnboardingAudience, startTour } from "../state/onboardingSlice";

export const ONBOARDING_WELCOME_MODAL_ID = "onboarding-welcome";
export const CREATOR_WELCOME_TOUR_ID = "creator-dashboard";
export const ENDUSER_WELCOME_TOUR_ID = "enduser-stats";

export const WelcomeModal = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const audience = useAppSelector(selectOnboardingAudience);
  const showStandardModal = useAppSelector(getShowStandardModal);
  const primaryButtonRef = useRef<HTMLElement | null>(null);
  const isEnduser = audience === "enduser";
  const welcomeTourId = isEnduser ? ENDUSER_WELCOME_TOUR_ID : CREATOR_WELCOME_TOUR_ID;
  const isOpen = showStandardModal.id === ONBOARDING_WELCOME_MODAL_ID && showStandardModal.show;
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setBusy(false);
    }
  }, [isOpen]);

  const handlePrimary = () => {
    if (busy) return;
    setBusy(true);
    void dispatch(applyOnboardingOps([{ op: "welcomeSeen" }]));
    enqueueOnboardingEvent({
      event: "welcome_cta",
      surface: "welcome",
      itemId: welcomeTourId,
      step: null,
    });
    dispatch(startTour({ id: welcomeTourId, source: "welcome" }));
    dispatch(closeStandardModal(ONBOARDING_WELCOME_MODAL_ID));
  };

  const handleSecondary = () => {
    if (busy) return;
    setBusy(true);
    void dispatch(applyOnboardingOps([{ op: "welcomeSeen" }]));
    enqueueOnboardingEvent({
      event: "welcome_dismissed",
      surface: "welcome",
      itemId: null,
      step: null,
    });
    dispatch(closeStandardModal(ONBOARDING_WELCOME_MODAL_ID));
  };

  return (
    <ModalStandard
      id={ONBOARDING_WELCOME_MODAL_ID}
      className="w-[30rem]"
      clickOnBackgroundClose={false}
      initialFocus={primaryButtonRef}
    >
      <div className="modalContent personalize-scroll-visible">
        <h2 className="pt-3 pb-1 font-medium text-lg text-balance">
          {t(isEnduser ? "onboarding.welcome.enduser.title" : "onboarding.welcome.creator.title")}
        </h2>
        <p className="pb-3 text-gray-600 whitespace-pre-line">
          {t(isEnduser ? "onboarding.welcome.enduser.body" : "onboarding.welcome.creator.body")}
        </p>
      </div>
      <div className="flex justify-end items-center mt-4 space-x-3">
        <button
          type="button"
          className="btn-alternative-outline"
          onClick={handleSecondary}
          disabled={busy}
        >
          {t("onboarding.welcome.secondary")}
        </button>
        <button
          ref={primaryButtonRef as React.RefObject<HTMLButtonElement>}
          type="button"
          className="btn-primary-fill"
          onClick={handlePrimary}
          disabled={busy}
        >
          {t("onboarding.welcome.primary")}
        </button>
      </div>
    </ModalStandard>
  );
};

export default WelcomeModal;
